import { useEffect, useRef, useState } from "react";

import { PaymentMethod } from "features/payment-methods/components";
import usePrimaryPaymentSource from "features/payment-methods/datahooks/usePrimaryPaymentSource";
import useSubscriptionPaymentSource from "features/payment-methods/datahooks/useSubscriptionPaymentSource";
import type { PaymentSource } from "features/payment-methods/types";
import ManageSubscriptionDetails from "features/subscriptions/components/ManageSubscription/ManageSubscriptionDetails";
import SubscriptionPaymentSource from "features/subscriptions/components/ManageSubscription/SubscriptionPaymentSource";
import SuccessLarge from "features/subscriptions/components/ManageSubscription/SuccessLarge";
import { AGENCY_INCLUDED_SEATS } from "features/subscriptions/constants";
import type useManageSubscriptionSteps from "features/subscriptions/hooks/useManageSubscriptionSteps";
import usePricingOptions from "features/subscriptions/hooks/usePricingOptions";
import useSubscriptions from "features/subscriptions/hooks/useSubscriptions";

import type { PricingPeriodInfo, SubscriptionType } from "common/types";

import Success from "./Success";

type UseManageSubscriptionStepsType = ReturnType<
  typeof useManageSubscriptionSteps
>;

interface ManageSubscriptionProps {
  plan: SubscriptionType;
  step: UseManageSubscriptionStepsType["manageSubscriptionStep"];
  goToStep: UseManageSubscriptionStepsType["goToManageSubscriptionStep"];
  initialSeatQuantity?: number;
}

export default function ManageSubscription({
  plan,
  step,
  goToStep,
  initialSeatQuantity = 0,
}: ManageSubscriptionProps) {
  //this is used by mobile to go to summary step directly
  const isPaymentPreviousStepRef = useRef<boolean>(false);

  const subscriptions = useSubscriptions();
  const subscriptionDetails = subscriptions[plan];

  const { subscriptionPaymentSource, isLoadingSubscriptionPaymentSource } =
    useSubscriptionPaymentSource(subscriptionDetails?.subscription.id);
  const { primaryPaymentSource, isLoadingPrimaryPaymentSource } =
    usePrimaryPaymentSource();
  const { pricingOptions } = usePricingOptions(
    plan,
    subscriptionDetails?.subscription,
  );

  const [paymentSource, setPaymentSource] = useState<PaymentSource>();
  const [quantity, setQuantity] = useState(
    initialSeatQuantity ||
      subscriptionDetails?.quantity ||
      (plan === "agency" ? AGENCY_INCLUDED_SEATS : 1),
  );
  const [pricingPeriodInfo, setPricingPeriodInfo] = useState<PricingPeriodInfo>(
    () => {
      if (subscriptionDetails) {
        const { period_unit, period } =
          subscriptionDetails.subscription.item_price;
        return { period, period_unit };
      }
      if (pricingOptions.length) {
        const { period_unit, period } = pricingOptions[0].plan;
        return { period, period_unit };
      }
    },
  );

  useEffect(() => {
    if (!pricingPeriodInfo && pricingOptions.length) {
      const { period_unit, period } = pricingOptions[0].plan;
      setPricingPeriodInfo({ period, period_unit });
    }
  }, [pricingOptions]);

  //set payment source to the subscription payment source OR the primary one
  useEffect(() => {
    if (!paymentSource) {
      if (subscriptionPaymentSource) {
        setPaymentSource(subscriptionPaymentSource);
      } else if (primaryPaymentSource) {
        setPaymentSource(primaryPaymentSource);
      }
    }
  }, [subscriptionPaymentSource, primaryPaymentSource, paymentSource]);

  if (step === "manage") {
    const paymentSourceComponent = (
      <SubscriptionPaymentSource
        paymentSource={paymentSource}
        onEdit={() => goToStep("paymentMethod")}
        isLoading={
          isLoadingSubscriptionPaymentSource || isLoadingPrimaryPaymentSource
        }
      />
    );

    return (
      <ManageSubscriptionDetails
        subscriptionType={plan}
        quantity={quantity}
        setQuantity={setQuantity}
        pricingPeriodInfo={pricingPeriodInfo}
        setPricingPeriodInfo={setPricingPeriodInfo}
        paymentSourceComponent={paymentSourceComponent}
        isPaymentPreviousStep={isPaymentPreviousStepRef.current}
        goToStep={goToStep}
        paymentSource={paymentSource}
      />
    );
  } else if (step === "paymentMethod") {
    return (
      <PaymentMethod
        initialPaymentSource={paymentSource}
        onBack={() => {
          isPaymentPreviousStepRef.current = true;
          goToStep("manage");
        }}
        setPaymentSource={(newPaymentSource) => {
          isPaymentPreviousStepRef.current = true;
          setPaymentSource(newPaymentSource);
          goToStep("manage");
        }}
      />
    );
  } else if (step === "success") {
    return <Success />;
  } else {
    return <SuccessLarge subscriptionType={plan} />;
  }
}
